import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NavLink } from './NavLink';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const menuVariants = {
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  open: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

const menuItemVariants = {
  closed: { x: -20, opacity: 0 },
  open: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: i * 0.1,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};

const dropdownVariants = {
  closed: {
    opacity: 0,
    y: -10,
    pointerEvents: 'none',
    transition: {
      duration: 0.2
    }
  },
  open: {
    opacity: 1,
    y: 0,
    pointerEvents: 'auto',
    transition: {
      duration: 0.2
    }
  }
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isEventsDropdownOpen, setIsEventsDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout>();
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  const eventsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = '';
  };

  const handleNavigation = (isHashLink: boolean) => {
    closeMenu();
    if (!isHashLink) {
      window.scrollTo(0, 0);
    }
  };

  const handleDropdownEnter = (dropdownType: 'about' | 'events') => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    if (dropdownType === 'about') {
      setIsAboutDropdownOpen(true);
    } else {
      setIsEventsDropdownOpen(true);
    }
  };

  const handleDropdownLeave = (dropdownType: 'about' | 'events') => {
    dropdownTimeoutRef.current = setTimeout(() => {
      const ref = dropdownType === 'about' ? aboutDropdownRef.current : eventsDropdownRef.current;
      if (!ref?.matches(':hover')) {
        if (dropdownType === 'about') {
          setIsAboutDropdownOpen(false);
        } else {
          setIsEventsDropdownOpen(false);
        }
      }
    }, 300);
  };

  const menuItems = [
    { path: '/', label: 'Home' },
    {
      path: '/#about',
      label: 'About',
      isHash: true,
      dropdown: [
        { path: '/facilitator', label: 'Facilitator' },
        { path: '/member', label: 'Member' }
      ]
    },
    { path: '/reading', label: 'Reading' },
    { path: '/events', label: 'Events' },
    { path: '/partnerships', label: 'Partnerships' },
    { path: '/#contact', label: 'Join', isHash: true }
  ];

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4">
        <nav className="flex justify-between items-center">
          <Link 
            to="/" 
            className="block"
            onClick={() => handleNavigation(false)}
          >
            <img
              src={isScrolled ? '/ChatGPT_Image_Sep_20,_2026,_07_14_20_PM.png' : '/ChatGPT_Image_Sep_20,_2026,_07_14_14_PM.png'}
              alt="Monday Night Group"
              className="h-14 md:h-16 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {menuItems.map(item => {
              if (item.dropdown) {
                return (
                  <div
                    key={item.path}
                    className="relative"
                    ref={item.label === 'About' ? aboutDropdownRef : eventsDropdownRef}
                    onMouseEnter={() => handleDropdownEnter(item.label === 'About' ? 'about' : 'events')}
                    onMouseLeave={() => handleDropdownLeave(item.label === 'About' ? 'about' : 'events')}
                  >
                    <button
                      className={`font-medium transition-colors duration-300 flex items-center ${
                        isScrolled ? 'text-gray-800 hover:text-blue-600' : 'text-white hover:text-gray-200'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </button>
                    <AnimatePresence>
                      {(item.label === 'About' ? isAboutDropdownOpen : isEventsDropdownOpen) && (
                        <motion.div
                          className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1"
                          variants={dropdownVariants}
                          initial="closed"
                          animate="open"
                          exit="closed"
                        >
                          {item.dropdown.map(dropdownItem => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              className="block px-4 py-2 text-gray-800 hover:bg-gray-100"
                              onClick={() => handleNavigation(false)}
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return item.isHash ? (
                <NavLink 
                  key={item.path} 
                  href={item.path} 
                  isScrolled={isScrolled}
                >
                  {item.label}
                </NavLink>
              ) : (
                <Link 
                  key={item.path}
                  to={item.path} 
                  className={`font-medium transition-colors duration-300 ${
                    isScrolled ? 'text-gray-800 hover:text-blue-600' : 'text-white hover:text-gray-200'
                  }`}
                  onClick={() => handleNavigation(false)}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <motion.button 
            className="md:hidden p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className={`h-6 w-6 ${isScrolled ? 'text-gray-800' : 'text-white'}`} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className={`h-6 w-6 ${isScrolled ? 'text-gray-800' : 'text-white'}`} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </nav>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="md:hidden fixed inset-x-0 top-[60px] bg-white shadow-lg overflow-hidden"
          >
            <nav className="container mx-auto px-4 py-4">
              <motion.div className="flex flex-col space-y-1">
                {menuItems.map((item, i) => (
                  <motion.div
                    key={item.path}
                    custom={i}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    {item.dropdown ? (
                      <>
                        <div className="block text-gray-800 px-4 py-3 text-lg font-medium">
                          {item.label}
                        </div>
                        {item.dropdown.map(dropdownItem => (
                          <Link
                            key={dropdownItem.path}
                            to={dropdownItem.path}
                            className="block text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg px-8 py-2 text-base font-medium transition-colors duration-200"
                            onClick={() => handleNavigation(false)}
                          >
                            {dropdownItem.label}
                          </Link>
                        ))}
                      </>
                    ) : item.isHash ? (
                      <a 
                        href={item.path}
                        className="block text-gray-800 hover:text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-3 text-lg font-medium transition-colors duration-200"
                        onClick={() => handleNavigation(true)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link 
                        to={item.path}
                        className="block text-gray-800 hover:text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-3 text-lg font-medium transition-colors duration-200"
                        onClick={() => handleNavigation(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;